import { SVG } from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { HLayout } from '@sleepstation/components-layouts'
import { LAYOUT_ALIGN, LAYOUT_SPACING } from '@sleepstation/const'
import {
  mq,
  offset,
  paddingH,
  paddingV,
  stylesIfElseProp,
} from '@sleepstation/style-api-helpers'
import { SleepstationMarkSvg } from '@sleepstation/svgs-sleepstation-branding'
import { rem } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { append, includes, map, mapObjIndexed, reduce } from 'ramda'
import React, { useState } from 'react'
import styled, { createGlobalStyle, css } from 'styled-components'
import BottomHorizontalMenu from './BottomHorizontalMenu'
import TopHorizontalMenu from './TopHorizontalMenu'
import VerticalMenu from './VerticalMenu'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const LOGO_WIDTHS = map(rem, [130, 191])

const MENU_HEIGHT_XS = rem(116)
const MENU_HEIGHT_S = rem(141)
const MENU_HEIGHT_M = rem(84)
const MENU_HEIGHT_L_UP = rem(83)

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const toggleVisibility = () =>
  stylesIfElseProp(
    `isVisible`,
    css`
      visibility: visible;
      opacity: 1;
      transition:
        visibility 0s linear 0s,
        opacity 500ms;
    `,
    css`
      visibility: hidden;
      opacity: 0;
      transition:
        visibility 0s linear 300ms,
        opacity 300ms;
    `
  )

const getTransformForMenu =
  () =>
  ({ menuIsOpen }) => {
    const offsetX = menuIsOpen ? `0` : `150%`
    return { transform: `translateX(${offsetX})` }
  }

const sortNavItems = reduce(
  (acc, navItem) =>
    mapObjIndexed((list, listName) => {
      const { menus } = navItem
      return includes(listName, menus) ? append(navItem, list) : list
    }, acc),
  { primaryMenu: [], secondary: [], primaryMenuLarge: [] }
)

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const LockScrollStyles = createGlobalStyle`
  html, body {
    overflow: hidden;
    height: 100%;
    max-height: 100%;
  }
`

const Layout = styled.div.attrs({
  'data-cy': `old-navbar`,
})`
  position: relative;

  /* Ensure header is in front of body */
  z-index: 2;
`

const MenuUnderlay = styled.div.attrs({ 'data-cy': `menu-underlay` })`
  position: fixed;
  z-index: 0;
  cursor: pointer;
  ${offset(0)}
  background-color: rgba(157 157 157 / 80%);
  ${toggleVisibility()}
  top: ${MENU_HEIGHT_XS};

  ${mq(`smallOnly`)} {
    top: ${MENU_HEIGHT_S};
  }

  ${mq(`mediumUp`)} {
    top: ${MENU_HEIGHT_M};
  }

  ${mq(`largeOnly`)} {
    top: ${MENU_HEIGHT_L_UP};
  }
`

const TopWrapper = styled(HLayout)`
  position: relative;
  z-index: 3;

  svg {
    fill: white;
  }

  ${paddingH([
    [0.75, 0.25],
    [1.25, 0.75],
  ])}

  ${paddingV([0.75, 1.1, 1.15, 1.05])}
`

const Tray = styled.div.attrs({ 'data-cy': `tray` })`
  position: fixed;
  right: 0;
  height: 100vh;
  transition: transform 300ms ease-in;
  background-color: #032841;
  top: ${MENU_HEIGHT_XS};

  ${mq(`smallOnly`)} {
    top: ${MENU_HEIGHT_S};
  }

  ${mq(`mediumOnly`)} {
    top: ${MENU_HEIGHT_M};
  }

  ${mq(`largeUp`)} {
    top: ${MENU_HEIGHT_L_UP};
  }

  ${getTransformForMenu()}
`

const LogoLink = styled(LinkBase)``

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const Navbar = ({ links, logoLink }) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  const { primaryMenu, secondary, primaryMenuLarge } = sortNavItems(links)

  return (
    <Layout>
      {menuIsOpen && <LockScrollStyles />}
      <TopWrapper justify={LAYOUT_SPACING.BETWEEN} align={LAYOUT_ALIGN.CENTER}>
        <LogoLink href={logoLink} forceOpenInSameWindow>
          <SVG svg={SleepstationMarkSvg} widths={LOGO_WIDTHS} />
        </LogoLink>
        <TopHorizontalMenu
          links={primaryMenuLarge}
          menuIsOpen={menuIsOpen}
          setMenuIsOpen={setMenuIsOpen}
        />
      </TopWrapper>
      <BottomHorizontalMenu links={primaryMenu} />
      <MenuUnderlay
        isVisible={menuIsOpen}
        onClick={() => setMenuIsOpen(false)}
      />
      <Tray menuIsOpen={menuIsOpen}>
        <VerticalMenu links={secondary} setMenuIsOpen={setMenuIsOpen} />
      </Tray>
    </Layout>
  )
}

Navbar.propTypes = {
  links: PropTypes.arrayOf(PropTypes.object).isRequired,
  logoLink: PropTypes.string.isRequired,
}

export default Navbar
